/** Add / Edit Project — port of the original projectModal(). Once a deadline is saved it can only be changed through Change Deadline. */
import { useState } from 'react';
import { PROJECT_STATUSES, type Project, type ProjectInput, type ProjectStatus } from '@/types/models';
import { fmtDate } from '@/domain/format';
import { deadline } from '@/domain/projects';
import { LIMITS, validateProject, type FieldErrors } from '@/domain/validation';
import { useAction, useDataset } from '@/hooks/data';
import { ErrorState, Field, LoadingState, ModalFrame } from '@/ui/kit';

const EMPTY: Project = {
  id: '',
  name: '',
  service: '',
  lead: '',
  status: 'In Progress',
  planned: '',
  revised: '',
  completed: '',
  pending: '',
  delayReason: '',
  extensions: [],
  links: [],
  members: [],
  metricKeys: [],
  metricKinds: {},
};

export function ProjectFormModal({ id, onClose, onChangeDeadline }: { id?: string; onClose: () => void; onChangeDeadline: (projectId: string) => void }) {
  const ds = useDataset();
  const existing = id ? ds.data?.projects.find((p) => p.id === id) : undefined;
  const title = `${id ? 'Edit' : 'Add'} Project`;
  if (ds.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={ds.error} onRetry={ds.refetch} /></ModalFrame>;
  if (!ds.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
  if (id && !existing) return <ModalFrame title={title} onClose={onClose}><div className="empty">No records found.</div></ModalFrame>;
  return <ProjectForm key={id || 'new'} project={existing || EMPTY} isEdit={!!id} title={title} onClose={onClose} onChangeDeadline={onChangeDeadline} />;
}

type FormState = Required<ProjectInput>;

function ProjectForm({ project: p, isEdit, title, onClose, onChangeDeadline }: { project: Project; isEdit: boolean; title: string; onClose: () => void; onChangeDeadline: (projectId: string) => void }) {
  // An edited project that already has a deadline shows it locked; a reason is needed to change it.
  const locked = isEdit && !!p.planned;
  const [f, setF] = useState<FormState>({
    name: p.name,
    lead: p.lead,
    service: p.service,
    status: p.status,
    planned: locked ? deadline(p) : p.planned,
    completed: p.completed,
    pending: p.pending,
  });
  const [err, setErr] = useState<FieldErrors>({});
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const save = useAction(
    (s, rec: ProjectInput) => (isEdit ? s.projects.update(p.id, rec) : s.projects.create(rec)),
    isEdit ? 'Project updated successfully.' : 'Project created successfully.',
  );
  const submit = async () => {
    const e = validateProject(f);
    setErr(e);
    if (Object.keys(e).length) return;
    const rec: ProjectInput = { name: f.name.trim(), lead: f.lead.trim(), service: f.service.trim(), status: f.status, completed: f.completed.trim(), pending: f.pending.trim() };
    if (!locked) rec.planned = f.planned.trim();
    const ok = await save.run(rec);
    if (ok) onClose();
  };
  return (
    <form onSubmit={(e) => (e.preventDefault(), void submit())} noValidate>
      <ModalFrame
        title={title}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={save.pending}>
              {isEdit ? 'Update' : 'Create'}
            </button>
          </>
        }
      >
        <div className="grid2">
          <Field label="Project Name" required error={err.name}>
            <input name="name" value={f.name} onChange={set('name')} placeholder="Enter Project Name" maxLength={LIMITS.projectName} />
          </Field>
          <Field label="Lead / Owner">
            <input name="lead" value={f.lead} onChange={set('lead')} placeholder="Enter Lead Name" maxLength={LIMITS.lead} />
          </Field>
        </div>
        <Field label="Service">
          <input name="service" value={f.service} onChange={set('service')} placeholder="What does this project deliver?" maxLength={LIMITS.service} />
        </Field>
        <div className="grid2">
          <Field label="Current Status">
            <select name="status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as ProjectStatus })}>
              {PROJECT_STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>
          <div></div>
          <Field
            label="Deadline (target completion date)"
            hint={
              locked ? (
                <>
                  Locked.{' '}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      onChangeDeadline(p.id);
                    }}
                  >
                    Change deadline
                  </a>{' '}
                  — a reason is required.
                </>
              ) : (
                'Once saved, this can only be changed by giving a reason.'
              )
            }
          >
            <input type="date" name="planned" value={f.planned} onChange={set('planned')} disabled={locked} />
          </Field>
          <Field label="Current deadline">
            <input type="text" value={fmtDate(deadline(p))} disabled />
          </Field>
        </div>
        <Field label="Completed">
          <textarea name="completed" maxLength={LIMITS.completed} placeholder="What is done" value={f.completed} onChange={set('completed')} />
        </Field>
        <Field label="Pending">
          <textarea name="pending" maxLength={LIMITS.pending} placeholder="What is left" value={f.pending} onChange={set('pending')} />
        </Field>
      </ModalFrame>
    </form>
  );
}
